import React, { useCallback, useEffect, useState } from 'react';
import { FileWarning, Check, X, MessageSquare, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '../lib/api';

/**
 * Inline list of pending attendance-regularization requests, visible only to HR / MD /
 * super_admin. Each request has three actions:
 *
 *   ✓ Approve            — applies a 'P' (Present) entry on the requested date.
 *   ✗ Reject             — captures optional reason.
 *   ? Ask for Clarification — adds a note thread; status flips back to Pending when
 *     the requester replies.
 */
export default function RegularizationApprovals() {
  const [items, setItems] = useState([]);
  const [isApprover, setIsApprover] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [activeReq, setActiveReq] = useState(null); // current in-modal request (for reject/clarify)
  const [actionType, setActionType] = useState(null); // 'reject' | 'clarify'
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiGet('/hr/regularization/pending-for-me');
      setIsApprover(!!res.is_approver);
      setItems(res.requests || []);
    } catch {
      setIsApprover(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onApprove = async (req) => {
    try {
      await apiPut(`/hr/regularization/${req.id}/approve`);
      toast.success(`Approved · ${req.employee_name} · ${req.date}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve');
    }
  };

  const openAction = (req, type) => {
    setActiveReq(req);
    setActionType(type);
    setNote('');
  };

  const submitAction = async () => {
    if (!activeReq || !actionType) return;
    if (!note.trim() && actionType === 'clarify') {
      toast.error('Please type your question for the requester');
      return;
    }
    try {
      if (actionType === 'reject') {
        await apiPut(`/hr/regularization/${activeReq.id}/reject`, { reason: note });
        toast.success('Rejected');
      } else if (actionType === 'clarify') {
        await apiPut(`/hr/regularization/${activeReq.id}/clarify`, { note });
        toast.success('Clarification requested');
      }
      setActiveReq(null); setActionType(null); setNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  if (!isApprover || items.length === 0) return null;

  return (
    <div className="mb-5 border border-amber-200 bg-amber-50/40 rounded-md" data-testid="regularization-approvals">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-50/60"
      >
        <div className="flex items-center gap-2">
          <FileWarning size={16} className="text-amber-700" />
          <span className="font-display font-bold text-sm text-amber-900">Attendance Regularization · {items.length} pending</span>
        </div>
        {expanded ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
      </button>

      {expanded && (
        <div className="px-2 pb-3 space-y-1.5">
          {items.map((r) => {
            const isClarification = r.status === 'Clarification Requested';
            return (
              <div key={r.id} className="bg-white border border-amber-100 rounded-sm p-3" data-testid={`reg-req-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-sm text-slate-900">{r.employee_name}</span>
                      <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{r.emp_code} · {r.department || '—'}</span>
                      {isClarification && (
                        <span className="text-[10px] font-display font-bold uppercase tracking-wider px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-sm">Clarification Sent</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-700 mt-1">
                      <b>{r.date}</b> · {r.request_type}
                      {r.check_in && <> · In <b>{r.check_in}</b></>}
                      {r.check_out && <> · Out <b>{r.check_out}</b></>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1 italic">"{r.reason}"</div>
                    <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><Clock size={10} /> Submitted {new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })} by {r.requested_by}</div>
                    {(r.clarifications || []).length > 0 && (
                      <div className="mt-2 border-t border-slate-100 pt-2 space-y-1">
                        {r.clarifications.map((c, i) => (
                          <div key={i} className="text-[11px] text-slate-700">
                            <span className="font-display font-bold uppercase tracking-wider text-[10px] text-[#1F7A6E]">{c.from_role === 'approver' ? '↳ ASKED' : '↳ REPLIED'}</span>{' '}
                            <span className="text-slate-500">{c.from}:</span>{' '}{c.note}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button onClick={() => onApprove(r)} className="text-[11px] font-display font-bold uppercase tracking-wider px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1" data-testid={`reg-approve-${r.id}`}>
                      <Check size={11} /> Approve
                    </button>
                    <button onClick={() => openAction(r, 'reject')} className="text-[11px] font-display font-bold uppercase tracking-wider px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1" data-testid={`reg-reject-${r.id}`}>
                      <X size={11} /> Reject
                    </button>
                    <button onClick={() => openAction(r, 'clarify')} className="text-[11px] font-display font-bold uppercase tracking-wider px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid={`reg-clarify-${r.id}`}>
                      <MessageSquare size={11} /> Clarify
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeReq && actionType && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#1F7A6E]">{activeReq.employee_name} · {activeReq.date}</div>
              <h3 className="font-display font-bold text-lg">{actionType === 'reject' ? 'Reject Request' : 'Ask for Clarification'}</h3>
            </div>
            <div className="p-5 space-y-3">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={4}
                placeholder={actionType === 'reject' ? 'Reason for rejection (optional but recommended)' : 'What clarification do you need from the requester?'}
                className="input resize-none"
                autoFocus
              />
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <button onClick={() => { setActiveReq(null); setActionType(null); }} className="btn-secondary">Cancel</button>
              <button onClick={submitAction} className={`btn-primary ${actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}`}>
                {actionType === 'reject' ? 'Reject Request' : 'Send Clarification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
